// Inpaint editor — paint a mask over an existing gallery image, type
// a prompt, send to imageGen.inpaint.
//
// Layout: full-screen modal. Left = canvas pane (image + mask
// overlay). Right = controls (prompt, brush size, eraser, useLora,
// useFillModel, denoise, optional reference).
//
// Canvas trick: a single offscreen <canvas> sized to source dimensions
// holds the binary mask (white = paint, transparent = keep). The DOM
// shows it CSS-scaled to fit. Pointer coords get mapped back to source
// resolution so brush strokes stay sharp regardless of display zoom.

import { useEffect, useRef, useState } from "preact/hooks";
import { Icon } from "../../icons";
import type { GalleryItem, ImageGenInpaintArgs } from "../../lib/rpc";

interface InpaintEditorProps {
  source: GalleryItem;
  onClose: () => void;
  onCommit: (args: ImageGenInpaintArgs) => Promise<GalleryItem | null>;
  notify: (msg: string) => void;
}

export function InpaintEditor({ source, onClose, onCommit, notify }: InpaintEditorProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const maskCanvasRef = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const lastXY = useRef<{ x: number; y: number } | null>(null);

  // Source dimensions — prefer the gallery settings (already at the
  // server-emitted resolution); fall back to the loaded <img> natural
  // size if settings are missing on legacy rows.
  const settingsW = Number((source.settings as any)?.width);
  const settingsH = Number((source.settings as any)?.height);
  const [srcW, setSrcW] = useState<number>(Number.isFinite(settingsW) && settingsW > 0 ? settingsW : 0);
  const [srcH, setSrcH] = useState<number>(Number.isFinite(settingsH) && settingsH > 0 ? settingsH : 0);

  const [prompt, setPrompt] = useState("");
  const [neg, setNeg] = useState("");
  const [brushSize, setBrushSize] = useState(48);
  const [erasing, setErasing] = useState(false);
  const [useLora, setUseLora] = useState(true);
  const [useFillModel, setUseFillModel] = useState(false);
  const [denoise, setDenoise] = useState(0.85);
  const [submitting, setSubmitting] = useState(false);
  const [hasMask, setHasMask] = useState(false);

  // Initialize the mask canvas the moment we know source dims. Re-runs
  // if the user navigates between sources (parent re-keys the modal).
  useEffect(() => {
    if (!srcW || !srcH || !maskCanvasRef.current) return;
    maskCanvasRef.current.width = srcW;
    maskCanvasRef.current.height = srcH;
    const ctx = maskCanvasRef.current.getContext("2d");
    if (ctx) ctx.clearRect(0, 0, srcW, srcH);
    setHasMask(false);
  }, [srcW, srcH]);

  // Escape closes — but only when not actively painting.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !drawing.current) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Convert a pointer event to source-resolution coords by reading the
  // canvas's current display rect.
  const toSrcXY = (e: PointerEvent): { x: number; y: number } => {
    const canvas = maskCanvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const sx = (e.clientX - rect.left) * (canvas.width / rect.width);
    const sy = (e.clientY - rect.top) * (canvas.height / rect.height);
    return { x: sx, y: sy };
  };

  // Stroke segment between (last) and (now) so fast drags don't leave
  // gaps when the pointer events come in slower than the visual move.
  const drawAt = (cur: { x: number; y: number }) => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const radius = brushSize / 2;
    // Translate brush size from display px → source px so the brush
    // feels consistent regardless of how the canvas is scaled in the
    // DOM. We use the canvas's intrinsic / display ratio.
    const rect = canvas.getBoundingClientRect();
    const scale = rect.width > 0 ? canvas.width / rect.width : 1;
    const radSrc = radius * scale;

    if (erasing) {
      ctx.globalCompositeOperation = "destination-out";
      ctx.fillStyle = "rgba(0,0,0,1)";
      ctx.strokeStyle = "rgba(0,0,0,1)";
    } else {
      ctx.globalCompositeOperation = "source-over";
      // White, fully opaque — the server-side mask reader treats any
      // non-zero alpha pixel as "paint here", but using white matches
      // the legacy convention so debugging the mask PNG is easier.
      ctx.fillStyle = "rgba(255,255,255,1)";
      ctx.strokeStyle = "rgba(255,255,255,1)";
    }
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.lineWidth = radSrc * 2;

    if (lastXY.current) {
      ctx.beginPath();
      ctx.moveTo(lastXY.current.x, lastXY.current.y);
      ctx.lineTo(cur.x, cur.y);
      ctx.stroke();
    } else {
      ctx.beginPath();
      ctx.arc(cur.x, cur.y, radSrc, 0, Math.PI * 2);
      ctx.fill();
    }
    lastXY.current = cur;
    setHasMask(true);
  };

  const onPointerDown = (e: PointerEvent) => {
    if (e.button !== 0 && e.pointerType === "mouse") return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drawing.current = true;
    lastXY.current = null;
    drawAt(toSrcXY(e));
  };
  const onPointerMove = (e: PointerEvent) => {
    if (!drawing.current) return;
    drawAt(toSrcXY(e));
  };
  const onPointerUp = () => {
    drawing.current = false;
    lastXY.current = null;
  };

  const clearMask = () => {
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setHasMask(false);
  };

  const submit = async () => {
    const text = prompt.trim();
    if (!text || !hasMask || submitting) return;
    if (!srcW || !srcH) { notify("Source dimensions missing."); return; }
    const canvas = maskCanvasRef.current;
    if (!canvas) return;
    // Export the mask as a binary PNG. Pixels with any alpha become
    // white in the server's reader; transparent stays untouched.
    const maskDataUrl = canvas.toDataURL("image/png");
    setSubmitting(true);
    try {
      const item = await onCommit({
        sourcePath: source.workspaceRelativePath,
        sourceWidth: srcW,
        sourceHeight: srcH,
        maskDataUrl,
        prompt: text,
        negativePrompt: neg.trim() || undefined,
        useLora,
        useFillModel,
        denoise,
      });
      if (item) onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="studio-modal-bg" onClick={(e) => { if (e.target === e.currentTarget && !drawing.current) onClose(); }}>
      <div className="studio-inpaint" onClick={(e) => e.stopPropagation()}>
        <div className="studio-inpaint-canvas" ref={wrapRef}>
          <img
            ref={imgRef}
            className="studio-inpaint-img"
            src={`/api/gallery-img/${source.id}`}
            alt="source"
            onLoad={() => {
              const img = imgRef.current;
              if (img && (!srcW || !srcH)) {
                setSrcW(img.naturalWidth);
                setSrcH(img.naturalHeight);
              }
            }}
          />
          <canvas
            ref={maskCanvasRef}
            className="studio-inpaint-mask"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
          />
        </div>

        <div className="studio-inpaint-side">
          <div className="studio-modal-head">
            <div>
              <h3 className="studio-inpaint-title">Inpaint</h3>
              <div className="studio-inpaint-sub">Paint a mask, type what should change.</div>
            </div>
            <button className="icon-btn" onClick={onClose} title="Close" type="button">
              <Icon name="x" size={14} />
            </button>
          </div>

          <div className="studio-row">
            <label className="studio-row-label">Prompt</label>
            <textarea
              className="input"
              rows={2}
              placeholder="Describe what should replace the masked area…"
              value={prompt}
              onInput={(e) => setPrompt((e.currentTarget as HTMLTextAreaElement).value)}
              onKeyDown={(e) => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
            />
          </div>

          <div className="studio-row">
            <label className="studio-row-label">Brush</label>
            <div className="studio-inpaint-brush-row">
              <button
                type="button"
                className="studio-chip"
                data-active={!erasing}
                onClick={() => setErasing(false)}
                title="Paint mask"
              >
                <Icon name="edit" size={12} /> Paint
              </button>
              <button
                type="button"
                className="studio-chip"
                data-active={erasing}
                onClick={() => setErasing(true)}
                title="Erase from mask"
              >
                <Icon name="x" size={12} /> Erase
              </button>
              <button
                type="button"
                className="studio-chip"
                onClick={clearMask}
                disabled={!hasMask}
                title="Clear the entire mask"
              >
                <Icon name="trash" size={12} /> Clear
              </button>
            </div>
            <div className="studio-ref-row" style={{ marginTop: 6 }}>
              <label className="studio-ref-slider-label">Size</label>
              <input
                type="range"
                min={6} max={200} step={2}
                value={brushSize}
                onInput={(e) => setBrushSize(Number((e.currentTarget as HTMLInputElement).value))}
              />
              <span className="studio-ref-slider-val">{brushSize}px</span>
            </div>
          </div>

          <div className="studio-row">
            <label className="studio-row-label">Negative (optional)</label>
            <input
              className="input"
              placeholder="Things to avoid in the new area…"
              value={neg}
              onInput={(e) => setNeg((e.currentTarget as HTMLInputElement).value)}
            />
          </div>

          <div className="studio-row">
            <label className="studio-row-label">Denoise</label>
            <div className="studio-ref-row">
              <input
                type="range"
                min={0.3} max={1} step={0.05}
                value={denoise}
                onInput={(e) => setDenoise(Number((e.currentTarget as HTMLInputElement).value))}
              />
              <span className="studio-ref-slider-val">{denoise.toFixed(2)}</span>
            </div>
            <div className="studio-inpaint-hint">
              Lower keeps more of the source under the mask; higher gives the model more freedom.
            </div>
          </div>

          <div className="studio-row">
            <label className="studio-toggle">
              <input
                type="checkbox"
                checked={useLora}
                onChange={(e) => setUseLora((e.currentTarget as HTMLInputElement).checked)}
              />
              Apply style LoRA (Dark-Fantasy bias)
            </label>
            <label className="studio-toggle">
              <input
                type="checkbox"
                checked={useFillModel}
                onChange={(e) => setUseFillModel((e.currentTarget as HTMLInputElement).checked)}
              />
              Use Fill workflow (better for big or detailed masks)
            </label>
          </div>

          <div className="studio-inpaint-foot">
            <button className="btn" onClick={onClose} type="button">Cancel</button>
            <button
              className="btn btn-primary"
              onClick={submit}
              disabled={!prompt.trim() || !hasMask || submitting}
              type="button"
            >
              <Icon name="send" size={13} />
              {submitting ? "Inpainting…" : "Inpaint"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
